import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * A rede de segurança da tela.
 *
 * Sem ela, qualquer erro ao desenhar um componente — um campo que o servidor
 * deixou de mandar, um dado num formato inesperado — derruba o React inteiro,
 * e o aluno fica olhando uma página em branco, sem saber se foi a internet, o
 * aparelho ou ele. Aqui o erro fica contido: a pessoa lê o que aconteceu e tem
 * por onde continuar.
 *
 * O `resetKey` é o caminho da página: trocar de tela limpa o erro, para um
 * problema numa tela não prender a pessoa fora das outras.
 */
export class ErroNaTela extends Component<
  { children: ReactNode; resetKey?: string },
  { erro: Error | null; chaveDoErro?: string }
> {
  state: { erro: Error | null; chaveDoErro?: string } = { erro: null };

  static getDerivedStateFromError(erro: Error) {
    return { erro };
  }

  static getDerivedStateFromProps(
    props: { resetKey?: string },
    state: { erro: Error | null; chaveDoErro?: string },
  ) {
    // Mudou de página depois do erro: tenta de novo na página nova.
    if (state.erro && state.chaveDoErro !== undefined && state.chaveDoErro !== props.resetKey) {
      return { erro: null, chaveDoErro: undefined };
    }
    if (state.erro && state.chaveDoErro === undefined) {
      return { chaveDoErro: props.resetKey ?? "" };
    }
    return null;
  }

  componentDidCatch(erro: Error, info: ErrorInfo) {
    console.error("Erro ao desenhar a tela:", erro, info.componentStack);
  }

  render() {
    if (!this.state.erro) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted px-4" role="alert" data-testid="erro-na-tela">
        <div className="max-w-md text-center space-y-4">
          <div className="text-5xl" aria-hidden="true">
            🧩
          </div>
          <h1 className="text-2xl font-bold text-foreground">Esta tela não abriu direito</h1>
          <p className="text-muted-foreground">
            Não foi nada que você fez, e o que você já estudou continua guardado. Recarregue a página; se continuar,
            avise a equipe do curso.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg bg-primary px-5 py-2.5 font-medium text-primary-foreground shadow"
            >
              Recarregar a página
            </button>
            <a
              href={import.meta.env.BASE_URL}
              className="rounded-lg border border-border bg-background px-5 py-2.5 font-medium text-foreground"
            >
              Voltar ao início
            </a>
          </div>
        </div>
      </div>
    );
  }
}
